import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../../stores/authStore'
import talentService from '../../services/talentService'
import authService from '../../services/authService'
import seekerService from '../../services/seekerService'
import { MultiSelect } from '../../components/ui/MultiSelect'
import { CheckboxGroup, type CheckboxOption } from '../../components/ui/CheckboxGroup'
import { TimezoneSelect } from '../../components/ui/TimezoneSelect'
import { CreatableTagInput, type TagItem } from '../../components/ui/CreatableTagInput'
import { AcademicBackground } from '../../components/profile/AcademicBackground'
import { ProfilePictureUpload } from '../../components/profile/ProfilePictureUpload'
import toast from 'react-hot-toast'
import type { SeekerProfile, OrganizationType, RequestType } from '../../types/seeker'
import { ORGANIZATION_TYPE_OPTIONS, REQUEST_TYPE_OPTIONS } from '../../types/seeker'
import type {
  TalentProfileFull,
  ProfileOption,
  Language,
  Country,
  ExpertiseOffering,
  Currency,
  TimezoneResponse,
  AcademicDegree,
} from '../../types/talent'

function SeekerProfileForm() {
  const { user, updateUser } = useAuthStore()
  const [seekerProfile, setSeekerProfile] = useState<SeekerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingPicture, setUploadingPicture] = useState(false)

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
    company_website: '',
    industry: '',
    description: '',
    organization_type: '' as OrganizationType | '',
    interested_in: [] as RequestType[],
    country: '',
    city: '',
  })

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true)
        const profile = await seekerService.getMyProfile()
        setSeekerProfile(profile)
        setFormData({
          first_name: profile.user_first_name || '',
          last_name: profile.user_last_name || '',
          email: profile.user_email || '',
          company_name: profile.company_name || '',
          company_website: profile.company_website || '',
          industry: profile.industry || '',
          description: profile.description || '',
          organization_type: profile.organization_type || '',
          interested_in: profile.interested_in || [],
          country: profile.country || '',
          city: profile.city || '',
        })
      } catch (err) {
        toast.error('Failed to load profile')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadProfile()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFormData((prev) => ({ ...prev, [name]: value as any }))
  }

  const handleInterestedInToggle = (value: RequestType) => {
    setFormData((prev) => ({
      ...prev,
      interested_in: prev.interested_in.includes(value)
        ? prev.interested_in.filter((v) => v !== value)
        : [...prev.interested_in, value],
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updated = await seekerService.updateMyProfile(formData)
      setSeekerProfile(updated)
      updateUser({
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email,
      })
      toast.success('Profile saved successfully')
    } catch (err) {
      toast.error('Failed to save profile')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleUploadPicture = async (file: File) => {
    try {
      setUploadingPicture(true)
      const result = await authService.uploadProfilePicture(file)
      updateUser({ profilePictureUrl: result.profile_picture_url ?? undefined })
      setSeekerProfile((prev) => prev ? { ...prev, profile_picture_url: result.profile_picture_url ?? null } : prev)
    } catch (err) {
      console.error('Failed to upload picture:', err)
      toast.error('Failed to upload profile picture. Please try again.')
    } finally {
      setUploadingPicture(false)
    }
  }

  const handleRemovePicture = async () => {
    try {
      setUploadingPicture(true)
      const result = await authService.deleteProfilePicture()
      updateUser({ profilePictureUrl: result.profile_picture_url ?? undefined })
      setSeekerProfile((prev) => prev ? { ...prev, profile_picture_url: null } : prev)
    } catch (err) {
      console.error('Failed to remove picture:', err)
      toast.error('Failed to remove profile picture. Please try again.')
    } finally {
      setUploadingPicture(false)
    }
  }

  const getInitials = () => {
    const first = formData.first_name?.[0] || ''
    const last = formData.last_name?.[0] || ''
    return (first + last).toUpperCase() || (user?.email?.[0]?.toUpperCase() || '?')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">My Profile</h1>
        <p className="text-secondary-600">Manage your organization profile</p>
      </div>

      <div className="space-y-6">
        {/* Profile Picture */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Profile Picture</h2>
          </div>
          <div className="card-body">
            <ProfilePictureUpload
              currentUrl={seekerProfile?.profile_picture_url}
              fallbackInitials={getInitials()}
              onUpload={handleUploadPicture}
              onRemove={handleRemovePicture}
              isUploading={uploadingPicture}
            />
          </div>
        </div>

        {/* Personal Info */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Personal Information</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  className="input"
                  placeholder="Jane"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  className="input"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="email"
                className="input"
                placeholder="jane@example.com"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Organization */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Organization</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="label">Organization Type</label>
              <select
                name="organization_type"
                className="input"
                value={formData.organization_type}
                onChange={handleChange}
              >
                <option value="">Select type...</option>
                {ORGANIZATION_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Company / Organization Name</label>
              <input
                type="text"
                name="company_name"
                className="input"
                placeholder="Acme Inc"
                value={formData.company_name}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="label">Website</label>
              <input
                type="url"
                name="company_website"
                className="input"
                placeholder="https://example.com"
                value={formData.company_website}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                name="industry"
                className="input"
                placeholder="Healthcare, Technology, etc."
                value={formData.industry}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="label">About</label>
              <textarea
                name="description"
                className="input min-h-[100px]"
                placeholder="Tell experts about your organization..."
                value={formData.description}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* What You're Looking For */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">What You're Looking For</h2>
          </div>
          <div className="card-body">
            <p className="text-sm text-secondary-500 mb-3">
              Select the types of collaboration you're interested in:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {REQUEST_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={formData.interested_in.includes(opt.value)}
                    onChange={() => handleInterestedInToggle(opt.value)}
                    className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Location</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Country</label>
                <input
                  type="text"
                  name="country"
                  className="input"
                  placeholder="United States"
                  value={formData.country}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="label">City</label>
                <input
                  type="text"
                  name="city"
                  className="input"
                  placeholder="New York"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}


export default function MyProfilePage() {
  const { user, updateUser } = useAuthStore()
  const isTalent = user?.role === 'talent'

  // Profile state
  const [profile, setProfile] = useState<TalentProfileFull | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingPicture, setUploadingPicture] = useState(false)

  // Options state
  const [mainFields, setMainFields] = useState<ProfileOption[]>([])
  const [workExpTypes, setWorkExpTypes] = useState<ProfileOption[]>([])
  const [experienceLevels, setExperienceLevels] = useState<ProfileOption[]>([])
  const [timesOfDay, setTimesOfDay] = useState<ProfileOption[]>([])
  const [meetingFormats, setMeetingFormats] = useState<ProfileOption[]>([])
  const [languages, setLanguages] = useState<Language[]>([])
  const [countries, setCountries] = useState<Country[]>([])
  const [expertiseOfferings, setExpertiseOfferings] = useState<ExpertiseOffering[]>([])
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [timezoneData, setTimezoneData] = useState<TimezoneResponse | null>(null)

  // Academic degrees state (CRUD, separate from form)
  const [academicDegrees, setAcademicDegrees] = useState<AcademicDegree[]>([])

  // Skills state (managed via add/remove, separate from form)
  const [profileSkills, setProfileSkills] = useState<TagItem[]>([])
  const [profileCredentials, setProfileCredentials] = useState<TagItem[]>([])

  // "Other" field inputs for Areas of Expertise
  const [otherHealthInput, setOtherHealthInput] = useState('')
  const [otherBiomedicalInput, setOtherBiomedicalInput] = useState('')
  const [addingOtherHealth, setAddingOtherHealth] = useState(false)
  const [addingOtherBiomedical, setAddingOtherBiomedical] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    // Basic info
    first_name: '',
    last_name: '',
    headline: '',
    bio: '',
    // Academic & Professional Background
    main_field_ids: [] as string[],
    experience_level_id: '',
    work_experience_type_ids: [] as string[],
    // Languages & Location
    language_ids: [] as string[],
    country_professional_ids: [] as string[],
    country_education_ids: [] as string[],
    // How You Can Help
    expertise_offering_ids: [] as string[],
    // Rates & Availability
    rate_currency_id: '',
    hourly_rate_min: '',
    hourly_rate_max: '',
    availability: 'available',
    preferred_time_ids: [] as string[],
    preferred_format_ids: [] as string[],
    timezone: '',
    // Publishing
    is_published: false,
  })

  // Load profile and options
  useEffect(() => {
    if (!isTalent) return

    const loadData = async () => {
      try {
        setLoading(true)
        const [
          profileData,
          mainFieldOpts,
          workExpOpts,
          expLevelOpts,
          timeOpts,
          formatOpts,
          languageList,
          countryList,
          expertiseList,
          currencyList,
          tzData,
        ] = await Promise.all([
          talentService.getMyProfile(),
          talentService.getProfileOptions('main_field'),
          talentService.getProfileOptions('work_experience_type'),
          talentService.getProfileOptions('experience_level'),
          talentService.getProfileOptions('time_of_day'),
          talentService.getProfileOptions('meeting_format'),
          talentService.getLanguages(),
          talentService.getCountries(),
          talentService.getExpertiseOfferings(),
          talentService.getCurrencies(),
          talentService.getTimezones(),
        ])

        setProfile(profileData)
        setMainFields(mainFieldOpts)
        setWorkExpTypes(workExpOpts)
        setExperienceLevels(expLevelOpts)
        setTimesOfDay(timeOpts)
        setMeetingFormats(formatOpts)
        setLanguages(languageList)
        setCountries(countryList)
        setExpertiseOfferings(expertiseList)
        setCurrencies(currencyList)
        setTimezoneData(tzData)

        // Set academic degrees
        setAcademicDegrees(profileData.academic_degrees || [])

        // Set skills and credentials as TagItems (split by category)
        const allSkills = profileData.skills || []
        setProfileSkills(
          allSkills
            .filter((ts) => ts.skill.category !== 'credential')
            .map((ts) => ({ id: ts.skill.id, name: ts.skill.name })),
        )
        setProfileCredentials(
          allSkills
            .filter((ts) => ts.skill.category === 'credential')
            .map((ts) => ({ id: ts.skill.id, name: ts.skill.name })),
        )

        // Set form data from profile
        const defaultCurrency = currencyList.find((c) => c.is_default)
        setFormData({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          headline: profileData.headline || '',
          bio: profileData.bio || '',
          main_field_ids: profileData.main_fields?.map((f) => String(f.id)) || [],
          experience_level_id: profileData.experience_level ? String(profileData.experience_level.id) : '',
          work_experience_type_ids: profileData.work_experience_types?.map((w) => String(w.id)) || [],
          language_ids: profileData.languages?.map((l) => String(l.id)) || [],
          country_professional_ids: profileData.countries_professional?.map((c) => String(c.id)) || [],
          country_education_ids: profileData.countries_education?.map((c) => String(c.id)) || [],
          expertise_offering_ids: profileData.expertise_offerings?.map((e) => String(e.id)) || [],
          rate_currency_id: profileData.rate_currency ? String(profileData.rate_currency.id) : (defaultCurrency ? String(defaultCurrency.id) : ''),
          hourly_rate_min: profileData.hourly_rate_min?.toString() || '',
          hourly_rate_max: profileData.hourly_rate_max?.toString() || '',
          availability: profileData.availability || 'available',
          preferred_time_ids: profileData.preferred_times?.map((t) => String(t.id)) || [],
          preferred_format_ids: profileData.preferred_formats?.map((f) => String(f.id)) || [],
          timezone: profileData.timezone || '',
          is_published: profileData.is_published || false,
        })
      } catch (err) {
        toast.error('Failed to load profile data')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [isTalent])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)

      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        headline: formData.headline,
        bio: formData.bio,
        main_field_ids: formData.main_field_ids,
        experience_level_id: formData.experience_level_id || null,
        work_experience_type_ids: formData.work_experience_type_ids,
        language_ids: formData.language_ids,
        country_professional_ids: formData.country_professional_ids,
        country_education_ids: formData.country_education_ids,
        expertise_offering_ids: formData.expertise_offering_ids,
        rate_currency_id: formData.rate_currency_id || null,
        hourly_rate_min: formData.hourly_rate_min ? parseFloat(formData.hourly_rate_min) : null,
        hourly_rate_max: formData.hourly_rate_max ? parseFloat(formData.hourly_rate_max) : null,
        availability: formData.availability as 'available' | 'open' | 'not_available',
        preferred_time_ids: formData.preferred_time_ids,
        preferred_format_ids: formData.preferred_format_ids,
        timezone: formData.timezone,
        is_published: formData.is_published,
      }

      const wasPublished = profile?.is_published
      const updatedProfile = await talentService.updateMyProfile(updateData as Partial<TalentProfileFull>)
      setProfile(updatedProfile)
      toast.success('Profile saved successfully')
      if (!wasPublished && updatedProfile.is_published) {
        toast.success('Profile is published')
      }
    } catch (err) {
      toast.error('Failed to save profile')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }, [formData, profile?.is_published])

  // === Areas of Expertise (main_fields) handlers ===
  const handleMainFieldsChange = (ids: string[]) => {
    setFormData((prev) => ({ ...prev, main_field_ids: ids }))
  }

  const handleAddOtherField = async (name: string, group: 'health' | 'biomedical') => {
    const trimmed = name.trim()
    if (!trimmed) return
    const order = group === 'health' ? 50 : 150
    const setter = group === 'health' ? setAddingOtherHealth : setAddingOtherBiomedical
    const inputSetter = group === 'health' ? setOtherHealthInput : setOtherBiomedicalInput
    try {
      setter(true)
      const option = await talentService.createProfileOption(trimmed, 'main_field', order)
      setMainFields((prev) => [...prev, option])
      setFormData((prev) => ({ ...prev, main_field_ids: [...prev.main_field_ids, String(option.id)] }))
      inputSetter('')
    } catch (err) {
      console.error('Failed to create field option:', err)
    } finally {
      setter(false)
    }
  }

  // Group main_fields into Health Sciences (order < 100) and Biomedical Sciences (order >= 100)
  const healthSciencesOptions: CheckboxOption[] = mainFields
    .filter((f) => f.order < 100)
    .map((f) => ({ id: String(f.id), label: f.name }))
  const biomedicalSciencesOptions: CheckboxOption[] = mainFields
    .filter((f) => f.order >= 100)
    .map((f) => ({ id: String(f.id), label: f.name }))

  // === Skills handlers ===
  const searchSkills = async (query: string): Promise<TagItem[]> => {
    const skills = await talentService.getSkills(query)
    return skills.map((s) => ({ id: s.id, name: s.name }))
  }

  const createSkill = async (name: string): Promise<TagItem> => {
    const skill = await talentService.createSkill(name)
    return { id: skill.id, name: skill.name }
  }

  const handleSkillAdd = async (item: TagItem) => {
    try {
      await talentService.addSkill(String(item.id))
      setProfileSkills((prev) => [...prev, item])
    } catch (err) {
      console.error('Failed to add skill:', err)
    }
  }

  const handleSkillRemove = async (item: TagItem) => {
    try {
      await talentService.removeSkill(String(item.id))
      setProfileSkills((prev) => prev.filter((s) => String(s.id) !== String(item.id)))
    } catch (err) {
      console.error('Failed to remove skill:', err)
    }
  }

  // === Credentials handlers ===
  const searchCredentials = async (query: string): Promise<TagItem[]> => {
    const skills = await talentService.getSkills(query, 'credential')
    return skills.map((s) => ({ id: s.id, name: s.name }))
  }

  const createCredential = async (name: string): Promise<TagItem> => {
    const skill = await talentService.createSkill(name, 'credential')
    return { id: skill.id, name: skill.name }
  }

  const handleCredentialAdd = async (item: TagItem) => {
    try {
      await talentService.addSkill(String(item.id))
      setProfileCredentials((prev) => [...prev, item])
    } catch (err) {
      console.error('Failed to add credential:', err)
    }
  }

  const handleCredentialRemove = async (item: TagItem) => {
    try {
      await talentService.removeSkill(String(item.id))
      setProfileCredentials((prev) => prev.filter((c) => String(c.id) !== String(item.id)))
    } catch (err) {
      console.error('Failed to remove credential:', err)
    }
  }

  // === Academic degree handlers ===
  const handleAddDegree = async (level: string) => {
    try {
      const degree = await talentService.createAcademicDegree({ level })
      setAcademicDegrees((prev) => [...prev, degree])
    } catch (err) {
      console.error('Failed to add degree:', err)
      toast.error('Failed to add degree. You may already have one at this level.')
    }
  }

  const handleUpdateDegree = async (
    id: string,
    data: { field_of_study?: string; institution_ids?: number[] },
  ) => {
    try {
      const updated = await talentService.updateAcademicDegree(id, data)
      setAcademicDegrees((prev) => prev.map((d) => (d.id === id ? updated : d)))
    } catch (err) {
      console.error('Failed to update degree:', err)
    }
  }

  const handleDeleteDegree = async (id: string) => {
    try {
      await talentService.deleteAcademicDegree(id)
      setAcademicDegrees((prev) => prev.filter((d) => d.id !== id))
    } catch (err) {
      console.error('Failed to delete degree:', err)
    }
  }


  // === Profile picture handlers ===
  const handleTalentUploadPicture = async (file: File) => {
    try {
      setUploadingPicture(true)
      const result = await authService.uploadProfilePicture(file)
      updateUser({ profilePictureUrl: result.profile_picture_url ?? undefined })
      setProfile((prev) => prev ? { ...prev, profile_picture_url: result.profile_picture_url ?? null } : prev)
    } catch (err) {
      console.error('Failed to upload picture:', err)
      toast.error('Failed to upload profile picture. Please try again.')
    } finally {
      setUploadingPicture(false)
    }
  }

  const handleTalentRemovePicture = async () => {
    try {
      setUploadingPicture(true)
      const result = await authService.deleteProfilePicture()
      updateUser({ profilePictureUrl: result.profile_picture_url ?? undefined })
      setProfile((prev) => prev ? { ...prev, profile_picture_url: null } : prev)
    } catch (err) {
      console.error('Failed to remove picture:', err)
      toast.error('Failed to remove profile picture. Please try again.')
    } finally {
      setUploadingPicture(false)
    }
  }

  const getTalentInitials = () => {
    const first = formData.first_name?.[0] || ''
    const last = formData.last_name?.[0] || ''
    return (first + last).toUpperCase() || (user?.email?.[0]?.toUpperCase() || '?')
  }

  // Convert expertise offerings to checkbox options with children
  const expertiseCheckboxOptions: CheckboxOption[] = expertiseOfferings.map((offering) => ({
    id: String(offering.id),
    label: offering.name,
    children: offering.sub_options?.map((sub) => ({
      id: String(sub.id),
      label: sub.name,
    })),
  }))

  if (!isTalent) {
    return <SeekerProfileForm />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold">My Profile</h1>
        <p className="text-secondary-600">
          Manage how your expertise is presented and discovered on LinkZon
        </p>
      </div>

      <div className="space-y-6">
        {/* Profile Completion */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Profile Completion</h2>
              <span className="text-sm text-secondary-500">{profile?.profile_completion || 0}%</span>
            </div>
            <div className="w-full bg-secondary-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${profile?.profile_completion || 0}%` }}
              />
            </div>
            <p className="text-sm text-secondary-600 mt-2">
              Complete your profile to increase visibility for relevant academic and professional requests.
            </p>
            {profile?.profile_completion_details && (
              <div className="mt-3 space-y-1">
                {profile.profile_completion_details.map((item) => (
                  <div key={item.section} className="flex items-center gap-2 text-sm">
                    {item.completed ? (
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    <span className={item.completed ? 'text-secondary-500' : 'text-amber-700 font-medium'}>
                      {item.section}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 1: Basic Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Basic Information</h2>
          </div>
          <div className="card-body space-y-4">
            <ProfilePictureUpload
              currentUrl={profile?.profile_picture_url}
              fallbackInitials={getTalentInitials()}
              onUpload={handleTalentUploadPicture}
              onRemove={handleTalentRemovePicture}
              isUploading={uploadingPicture}
            />
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">First Name (Private)</label>
                <input
                  type="text"
                  name="first_name"
                  className="input"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="label">Last Name (Private)</label>
                <input
                  type="text"
                  name="last_name"
                  className="input"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div>
              <label className="label">Expertise Headline</label>
              <input
                type="text"
                name="headline"
                className="input"
                placeholder="Medical Lab Scientist, Global Health Professional"
                value={formData.headline}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label className="label">Bio</label>
              <textarea
                name="bio"
                className="input min-h-[100px]"
                placeholder="5 years of experience in CLIA/CAP-certified hospital laboratories in the United States; former WHO intern in biosafety and quality control in Guatemala"
                value={formData.bio}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Academic & Professional Background */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Academic & Professional Background</h2>
          </div>
          <div className="card-body space-y-6">
            {/* 2a: Areas of Expertise */}
            <div>
              <label className="label">Areas of Expertise</label>
              <p className="text-sm text-secondary-500 mb-2">
                Select your academic/professional fields.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-secondary-700 mb-2">Health Sciences</h4>
                  <CheckboxGroup
                    options={healthSciencesOptions}
                    value={formData.main_field_ids}
                    onChange={handleMainFieldsChange}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={otherHealthInput}
                      onChange={(e) => setOtherHealthInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOtherField(otherHealthInput, 'health') } }}
                      placeholder="Other (type to add)"
                      className="input flex-1 text-sm py-1.5"
                      disabled={addingOtherHealth}
                    />
                    <button
                      type="button"
                      onClick={() => handleAddOtherField(otherHealthInput, 'health')}
                      disabled={!otherHealthInput.trim() || addingOtherHealth}
                      className="btn btn-secondary text-sm py-1.5 px-3"
                    >
                      {addingOtherHealth ? '...' : 'Add'}
                    </button>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-secondary-700 mb-2">Biomedical Sciences</h4>
                  <CheckboxGroup
                    options={biomedicalSciencesOptions}
                    value={formData.main_field_ids}
                    onChange={handleMainFieldsChange}
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={otherBiomedicalInput}
                      onChange={(e) => setOtherBiomedicalInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOtherField(otherBiomedicalInput, 'biomedical') } }}
                      placeholder="Other (type to add)"
                      className="input flex-1 text-sm py-1.5"
                      disabled={addingOtherBiomedical}
                    />
                    <button
                      type="button"
                      onClick={() => handleAddOtherField(otherBiomedicalInput, 'biomedical')}
                      disabled={!otherBiomedicalInput.trim() || addingOtherBiomedical}
                      className="btn btn-secondary text-sm py-1.5 px-3"
                    >
                      {addingOtherBiomedical ? '...' : 'Add'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 2b: Academic Background */}
            <div>
              <label className="label">Academic Background</label>
              <p className="text-sm text-secondary-500 mb-2">
                Add your academic degrees and institutions.
              </p>
              <AcademicBackground
                degrees={academicDegrees}
                onAdd={handleAddDegree}
                onUpdate={handleUpdateDegree}
                onDelete={handleDeleteDegree}
                onSearchInstitutions={talentService.getInstitutions}
                onCreateInstitution={talentService.createInstitution}
              />
            </div>

            {/* 2c: Professional Experience Context */}
            <div>
              <label className="label">Experience Level</label>
              <select
                name="experience_level_id"
                className="input"
                value={formData.experience_level_id}
                onChange={handleInputChange}
              >
                <option value="">Select experience level...</option>
                {experienceLevels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label">Work Experience Type</label>
              <p className="text-sm text-secondary-500 mb-2">
                Select all that apply:
              </p>
              <CheckboxGroup
                options={workExpTypes.map((w) => ({ id: String(w.id), label: w.name }))}
                value={formData.work_experience_type_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, work_experience_type_ids: ids }))}
              />
            </div>

            {/* 2d: Skills */}
            <div>
              <label className="label">Skills (Optional)</label>
              <p className="text-sm text-secondary-500 mb-2">
                Add specific skills relevant to your expertise. Type to create new ones.
              </p>
              <CreatableTagInput
                value={profileSkills}
                onSearch={searchSkills}
                onCreate={createSkill}
                onAdd={handleSkillAdd}
                onRemove={handleSkillRemove}
                placeholder="Search or type to add a skill..."
                createLabel="Create"
              />
            </div>

            {/* 2e: Credentials */}
            <div>
              <label className="label">Credentials (Optional)</label>
              <p className="text-sm text-secondary-500 mb-2">
                Add certifications, licenses, or professional credentials. Type to create new ones.
              </p>
              <CreatableTagInput
                value={profileCredentials}
                onSearch={searchCredentials}
                onCreate={createCredential}
                onAdd={handleCredentialAdd}
                onRemove={handleCredentialRemove}
                placeholder="Search or type to add a credential..."
                createLabel="Create"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Languages & Location */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Languages & Location</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="label">Languages Spoken</label>
              <MultiSelect
                options={languages.map((l) => ({ id: String(l.id), label: l.name }))}
                value={formData.language_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, language_ids: ids }))}
                placeholder="Select languages..."
              />
            </div>
            <div>
              <label className="label">Countries of Professional Experience</label>
              <MultiSelect
                options={countries.map((c) => ({ id: String(c.id), label: c.name }))}
                value={formData.country_professional_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, country_professional_ids: ids }))}
                placeholder="Select countries..."
              />
            </div>
            <div>
              <label className="label">Countries of Education</label>
              <MultiSelect
                options={countries.map((c) => ({ id: String(c.id), label: c.name }))}
                value={formData.country_education_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, country_education_ids: ids }))}
                placeholder="Select countries..."
              />
            </div>
          </div>
        </div>

        {/* Section 4: How You Can Help */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Services You Offer</h2>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="label">Expertise Offerings</label>
              <p className="text-sm text-secondary-500 mb-2">
                Select the types of support you can provide. Sub-options appear when relevant.
              </p>
              <CheckboxGroup
                options={expertiseCheckboxOptions}
                value={formData.expertise_offering_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, expertise_offering_ids: ids }))}
              />
            </div>
          </div>
        </div>

        {/* Section 5: Rates & Availability */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold">Rates & Availability</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="label">Currency</label>
                <select
                  name="rate_currency_id"
                  className="input"
                  value={formData.rate_currency_id}
                  onChange={handleInputChange}
                >
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code} ({currency.symbol})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Minimum Rate/hr</label>
                <input
                  type="number"
                  name="hourly_rate_min"
                  className="input"
                  placeholder="10"
                  value={formData.hourly_rate_min}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="label">Maximum Rate/hr</label>
                <input
                  type="number"
                  name="hourly_rate_max"
                  className="input"
                  placeholder="80"
                  value={formData.hourly_rate_max}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <p className="text-sm text-secondary-500">
              Set your hourly range based on the type and complexity of the assistance requested
            </p>

            <div>
              <label className="label">Preferred Time of Day</label>
              <CheckboxGroup
                options={timesOfDay.map((t) => ({ id: String(t.id), label: t.name }))}
                value={formData.preferred_time_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, preferred_time_ids: ids }))}
                columns={3}
              />
            </div>

            <div>
              <label className="label">Preferred Meeting Format</label>
              <CheckboxGroup
                options={meetingFormats.map((f) => ({ id: String(f.id), label: f.name }))}
                value={formData.preferred_format_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, preferred_format_ids: ids }))}
                columns={2}
              />
            </div>

            <div>
              <label className="label">Timezone</label>
              <TimezoneSelect
                value={formData.timezone}
                onChange={(tz) => setFormData((prev) => ({ ...prev, timezone: tz }))}
                timezones={timezoneData?.timezones || []}
                grouped={timezoneData?.grouped}
                autoDetect={!formData.timezone}
              />
            </div>
          </div>
        </div>

        {/* Publishing */}
        <div className="card">
          <div className="card-body">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="is_published"
                checked={formData.is_published}
                onChange={handleInputChange}
                className="h-5 w-5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="font-medium">Publish Profile</span>
                <p className="text-sm text-secondary-500">
                  Make your profile visible to organizations seeking expertise
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
